import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { errorMessage } from '../../lib/api';
import { STATUS_LABEL, type Status } from '../../lib/format';

// Refetch keeps the frame: data is replaced in place, never blanked while reloading.
export const usePoll = <T,>(load: () => Promise<T>, ms = 15_000) => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const reload = useCallback(
    () =>
      load()
        .then((d) => {
          setData(d);
          setError('');
        })
        .catch((e) => setError(errorMessage(e))),
    [load]
  );
  useEffect(() => {
    void reload();
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void reload();
    }, ms);
    return () => window.clearInterval(t);
  }, [reload, ms]);
  return { data, error, reload };
};

export const StatusPill = ({ status }: { status: Status }) => (
  <span className="pill" data-s={status}>
    {STATUS_LABEL[status]}
  </span>
);

export const LivePill = ({ n }: { n: number }) =>
  n > 0 ? (
    <span className="pill" data-s="live">
      {n === 1 ? 'Lendo agora' : `${n} lendo agora`}
    </span>
  ) : null;

export const Kpi = ({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'risk' }) => (
  <div className="kpi" data-tone={tone}>
    <p className="kpi-l">{label}</p>
    <p className="kpi-v">{value}</p>
    {sub && <p className="kpi-s">{sub}</p>}
  </div>
);

export const Card = ({ title, aside, children, className = '' }: { title: string; aside?: ReactNode; children: ReactNode; className?: string }) => (
  <section className={`card panel ${className}`}>
    <header className="card-h">
      <h2>{title}</h2>
      {aside}
    </header>
    {children}
  </section>
);

export const Empty = ({ children }: { children: ReactNode }) => <p className="empty">{children}</p>;

const PATHS: Record<string, ReactNode> = {
  key: <path d="M10.5 6.5a3 3 0 1 1-2.2 2.9L3 14.7V17h2.3v-1.6H7V13.7h1.6l1.3-1.3a3 3 0 0 1 .6-5.9zM12 8.2h.01" />,
  eye: (
    <>
      <path d="M2 10s3-5.5 8-5.5S18 10 18 10s-3 5.5-8 5.5S2 10 2 10z" />
      <circle cx="10" cy="10" r="2.4" />
    </>
  ),
  pin: <path d="M10 17s5-4.6 5-8.5a5 5 0 0 0-10 0C5 12.4 10 17 10 17zM10 10.2a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4z" />,
  down: <path d="M10 3v13M5 11l5 5 5-5" />,
  cam: (
    <>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h2l1.2-1.8h4.6L13.5 6h2A1.5 1.5 0 0 1 17 7.5v7A1.5 1.5 0 0 1 15.5 16h-11A1.5 1.5 0 0 1 3 14.5z" />
      <circle cx="10" cy="11" r="2.8" />
    </>
  ),
  tap: <path d="M8 9V4.5a1.5 1.5 0 0 1 3 0V9l3.6.9a1.7 1.7 0 0 1 1.3 1.9l-.6 4.2H8.6L5.4 12a1.4 1.4 0 0 1 2-1.9L8 10.8" />,
  ok: <path d="M4.5 10.5l3.5 3.5 7.5-8" />,
  edit: <path d="M4 16h3l8.5-8.5-3-3L4 13v3zM11 6l3 3" />,
  cal: (
    <>
      <rect x="3.5" y="4.5" width="13" height="12" rx="2" />
      <path d="M3.5 8.5h13M7 3v3M13 3v3" />
    </>
  ),
  renew: <path d="M15.5 9A5.5 5.5 0 0 0 5.2 6.4M4.5 11a5.5 5.5 0 0 0 10.3 2.6M5 3v3.5h3.5M15 17v-3.5h-3.5" />,
  live: <circle cx="10" cy="10" r="3.5" />,
};

export const Icon = ({ name, className }: { name: string; className?: string }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {PATHS[name] ?? PATHS.live}
  </svg>
);

export const Toast = ({ text, onDone }: { text: string; onDone: () => void }) => {
  useEffect(() => {
    const t = window.setTimeout(onDone, 2600);
    return () => window.clearTimeout(t);
  }, [onDone]);
  return (
    <div className="toast" role="status">
      {text}
    </div>
  );
};
